import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../App';
import { useAuth } from '../context/AuthContext';
import BottomNav from '../components/BottomNav';
import SearchBar from '../components/SearchBar';
import Onboarding, { isOnboardingCompleted } from '../components/Onboarding';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { BookOpen, Scale, Clock, Bell, Settings, FileText, BarChart3, HelpCircle, List, LogOut, FileSpreadsheet, Download, Eye } from 'lucide-react';
import { toast } from 'sonner';
import { trackActivity } from '../utils/trackActivity';

export default function LibraryPage() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [references, setReferences] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showOnboarding, setShowOnboarding] = useState(false);

  useEffect(() => {
    fetchReferences();
    fetchNotifications();
    
    // Track page view
    trackActivity('library_view', 'Main library page', 'Library');
    
    // Refetch notifications when page becomes visible (user returns from notifications page)
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        fetchNotifications();
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  useEffect(() => {
    // Show onboarding for new users - check when user is loaded
    if (user && !isOnboardingCompleted()) {
      setTimeout(() => setShowOnboarding(true), 1500); // Delay 1.5 seconds to ensure page is fully loaded
    }
  }, [user]);

  const fetchReferences = async () => {
    try {
      const response = await api.get('/references');
      setReferences(response.data);
    } catch (error) {
      toast.error('Failed to load references');
    } finally {
      setLoading(false);
    }
  };

  const fetchNotifications = async () => {
    try {
      const response = await api.get('/notifications');
      const unread = response.data.filter(n => !n.read);
      setNotifications(unread);
    } catch (error) {
      console.warn('Notifications fetch skipped:', error.message);
    }
  };

  const keyReferences = references
    .filter(r => ['RA 9184', 'RA 12009', 'RA 9184 IRR', 'RA 12009 IRR'].includes(r.short_name))
    .sort((a, b) => {
      // Define the desired order: RA 12009, RA 12009 IRR, RA 9184, RA 9184 IRR
      const order = ['RA 12009', 'RA 12009 IRR', 'RA 9184', 'RA 9184 IRR'];
      return order.indexOf(a.short_name) - order.indexOf(b.short_name);
    });
  
  const comparativeMatrices = references.filter(r => r.type === 'matrix');
  
  // Get all GPPB resolutions and sort by resolution number (03-2025 before 05-2025)
  const gppbResolutions = references
    .filter(r => r.type === 'resolution')
    .sort((a, b) => {
      // Extract resolution number for sorting (e.g., "03-2025" from "GPPB Resolution No. 03-2025")
      const getResNum = (shortName) => {
        const match = shortName?.match(/(\d+)-(\d+)/);
        return match ? parseInt(match[1]) : 999;
      };
      return getResNum(a.short_name) - getResNum(b.short_name);
    });

  const openReference = (refId) => {
    navigate(`/references/${refId}`);
  };

  const openMatrix = (type) => {
    if (type === 'sections') {
      navigate('/comparative-matrix');
    } else if (type === 'timelines') {
      navigate('/timeline-matrix');
    } else if (type === 'parallel-provisions') {
      navigate('/parallel-provisions');
    } else if (type === 'standard-forms') {
      navigate('/standard-forms');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-slate-100 to-emerald-50 dark:from-slate-900 dark:via-slate-800 dark:to-emerald-900 pb-24">
      {/* Onboarding Modal */}
      <Onboarding 
        isOpen={showOnboarding} 
        onClose={() => setShowOnboarding(false)} 
        referralToken={user?.referral_token}
      />
      
      {/* Green Top Banner with Logo */}
      <div className="bg-gradient-to-r from-emerald-700 to-emerald-600 shadow-lg">
        <div className="max-w-7xl mx-auto px-4 py-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-6">
              {/* Logo with Elegant Double-Line Gold Frame - Larger Size - Hidden on mobile */}
              <div className="relative p-3 hidden md:block">
                {/* Outer gold border */}
                <div className="absolute inset-0 rounded-lg" style={{ 
                  border: '2px solid #FFD700',
                  boxShadow: '0 0 20px rgba(255, 215, 0, 0.4)'
                }}></div>
                {/* Inner gold border */}
                <div className="absolute inset-2 rounded-lg" style={{ 
                  border: '1px solid #FFD700'
                }}></div>
                <img 
                  src="/sleek-logo.png" 
                  alt="LexProcure PH Logo" 
                  className="h-32 lg:h-36 w-auto relative z-10"
                />
              </div>
              {/* Brand Title */}
              <div>
                <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold" style={{ fontFamily: 'IBM Plex Sans' }}>
                  <span style={{ color: '#FFD700' }}>Lex</span>
                  <span className="text-white">Procure PH</span>
                </h1>
                <p className="text-sm md:text-base lg:text-lg text-emerald-50">Philippine Procurement Law Digital Reference</p>
              </div>
            </div>
            {/* Action Buttons */}
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="icon"
                className="relative text-white hover:bg-emerald-800"
                onClick={() => navigate('/notifications')}
                title="Notifications"
              >
                <Bell className="w-5 h-5" />
                {notifications.length > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                    {notifications.length}
                  </span>
                )}
              </Button>
              <Button 
                variant="ghost" 
                size="icon"
                className="text-white hover:bg-emerald-800"
                onClick={() => navigate('/help')}
                title="How to Use"
              >
                <HelpCircle className="w-5 h-5" />
              </Button>
              <Button 
                variant="ghost" 
                size="icon"
                className="text-white hover:bg-emerald-800"
                onClick={() => navigate('/settings')}
                title="Settings"
              >
                <Settings className="w-5 h-5" />
              </Button>
              <Button 
                variant="ghost" 
                size="icon"
                className="text-white hover:bg-emerald-800"
                onClick={() => {
                  logout();
                  navigate('/');
                  toast.success('Logged out successfully');
                }}
                title="Logout"
              >
                <LogOut className="w-5 h-5" />
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="mb-8">
          <h2 className="text-3xl lg:text-4xl font-bold text-slate-800 dark:text-slate-100 mb-2" style={{ fontFamily: 'IBM Plex Sans' }}>
            Library
          </h2>
          <p className="text-slate-600 dark:text-slate-400">Philippine Procurement Reference Collection</p>
        </div>

        <SearchBar />

        <div className="my-8">
          <Card className="bg-gradient-to-r from-emerald-600 to-emerald-700 text-white border-0 shadow-xl">
            <CardContent className="p-8">
              <h2 className="text-3xl font-bold mb-3" style={{ fontFamily: 'IBM Plex Sans' }}>
                Your Comprehensive Guide to Philippine Government Procurement
              </h2>
              <p className="text-emerald-100 text-lg">
                The fastest, most convenient way to access RA 12009, RA 9184, their IRRs, and curated cross-reference tools in one smart hub.
              </p>
            </CardContent>
          </Card>
        </div>

        <section className="mb-8">
          <div className="flex items-center gap-2 mb-4">
            <Scale className="text-emerald-600 dark:text-emerald-400" size={24} />
            <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-100">Key References</h2>
          </div>
          
          {loading ? (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400">Loading references...</div>
          ) : (
            <div className="grid gap-4 md:grid-cols-2">
              {keyReferences.map((ref) => (
                <Card
                  key={ref.id}
                  className="hover:shadow-lg transition-shadow cursor-pointer bg-white dark:bg-slate-800 dark:border-slate-700 dark:hover:border-emerald-600"
                  onClick={() => openReference(ref.id)}
                >
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <Scale className="text-emerald-600 dark:text-emerald-400 flex-shrink-0" size={24} />
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                        ref.status === 'active' 
                          ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200' 
                          : 'bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-200'
                      }`}>
                        {ref.status === 'active' ? 'Active' : ref.status === 'superseded' ? 'Superseded' : 'Pending'}
                      </span>
                    </div>
                    <CardTitle className="text-xl mt-2 dark:text-slate-100">{ref.short_name}</CardTitle>
                    <CardDescription className="dark:text-slate-400">{ref.title}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2 text-sm text-slate-600 dark:text-slate-400">
                      <p className="font-medium text-slate-700 dark:text-slate-300">{ref.description}</p>
                      <div className="space-y-1 pt-2 border-t border-slate-200 dark:border-slate-700">
                        {ref.metadata?.date_approved && (
                          <p><strong>Date Approved:</strong> {ref.metadata.date_approved}</p>
                        )}
                        {ref.metadata?.date_published && (
                          <p><strong>Date Published:</strong> {ref.metadata.date_published}</p>
                        )}
                        <p><strong>Date Effective:</strong> {ref.effectivity_date}</p>
                        {ref.metadata?.issuing_authority && (
                          <p><strong>Issuing Authority:</strong> {ref.metadata.issuing_authority}</p>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </section>

        {gppbResolutions.length > 0 && (
          <section className="mb-8">
            <div className="flex items-center gap-2 mb-4">
              <FileText className="text-emerald-600 dark:text-emerald-400" size={24} />
              <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-100">Relevant GPPB Issuances</h2>
            </div>
            
            <div className="grid gap-4 md:grid-cols-2">
              {gppbResolutions.map((resolution) => (
                <Card
                  key={resolution.id}
                  className="hover:shadow-lg transition-shadow cursor-pointer bg-white dark:bg-slate-800 dark:border-slate-700 dark:hover:border-emerald-600"
                  onClick={() => openReference(resolution.id)}
                >
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <FileText className="text-emerald-600 dark:text-emerald-400 flex-shrink-0" size={24} />
                      <span className="px-3 py-1 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200">
                        Active
                      </span>
                    </div>
                    <CardTitle className="text-xl mt-2 dark:text-slate-100">{resolution.short_name}</CardTitle>
                    <CardDescription className="dark:text-slate-400 mt-2">{resolution.title}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2 text-sm text-slate-600 dark:text-slate-400">
                      <p className="font-medium text-slate-700 dark:text-slate-300">{resolution.description}</p>
                      <div className="space-y-1 pt-2 border-t border-slate-200 dark:border-slate-700">
                        {resolution.metadata?.date_approved && (
                          <p><strong>Date Approved:</strong> {resolution.metadata.date_approved}</p>
                        )}
                        {resolution.metadata?.date_published && (
                          <p><strong>Date Published:</strong> {resolution.metadata.date_published}</p>
                        )}
                        <p><strong>Date Effective:</strong> {resolution.effectivity_date}</p>
                        {resolution.metadata?.issuing_authority && (
                          <p><strong>Issuing Authority:</strong> {resolution.metadata.issuing_authority}</p>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </section>
        )}

        <section className="mb-8">
          <div className="flex items-center gap-2 mb-4">
            <List className="text-emerald-600 dark:text-emerald-400" size={24} />
            <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-100">Quick Reference Matrices</h2>
          </div>
          
          <div className="grid gap-4 md:grid-cols-2">
            <Card
              className="hover:shadow-lg transition-shadow cursor-pointer bg-white dark:bg-slate-800 dark:border-slate-700 dark:hover:border-emerald-600"
              onClick={() => openMatrix('sections')}
            >
              <CardHeader>
                <div className="flex flex-col">
                  <List className="text-emerald-600 dark:text-emerald-400 mb-3" size={32} />
                  <CardTitle className="text-xl dark:text-slate-100">Section Titles</CardTitle>
                  <CardDescription className="dark:text-slate-400">RA 12009 vs RA 9184</CardDescription>
                </div>
                <div className="mt-4 border-t border-slate-200 dark:border-slate-700"></div>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-slate-600 dark:text-slate-400">
                  Side-by-side comparison of section titles showing provisions that are new, retained, amended, or deleted.
                </p>
              </CardContent>
            </Card>

            <Card
              className="hover:shadow-lg transition-shadow cursor-pointer bg-white dark:bg-slate-800 dark:border-slate-700 dark:hover:border-emerald-600"
              onClick={() => openMatrix('parallel-provisions')}
            >
              <CardHeader>
                <div className="flex flex-col">
                  <List className="text-emerald-600 dark:text-emerald-400 mb-3" size={32} />
                  <CardTitle className="text-xl dark:text-slate-100">Parallel Provisions</CardTitle>
                  <CardDescription className="dark:text-slate-400">RA 12009 vs RA 9184</CardDescription>
                </div>
                <div className="mt-4 border-t border-slate-200 dark:border-slate-700"></div>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-slate-600 dark:text-slate-400">
                  Side-by-side comparison of parallel provisions under RA 12009 and RA 9184, highlighting similarities, refinements, and provisions that are newly introduced or repealed in the new law.
                </p>
              </CardContent>
            </Card>

            <Card
              className="hover:shadow-lg transition-shadow cursor-pointer bg-white dark:bg-slate-800 dark:border-slate-700 dark:hover:border-emerald-600"
              onClick={() => openMatrix('timelines')}
            >
              <CardHeader>
                <div className="flex flex-col">
                  <List className="text-emerald-600 dark:text-emerald-400 mb-3" size={32} />
                  <CardTitle className="text-xl dark:text-slate-100">Procurement Timelines</CardTitle>
                  <CardDescription className="dark:text-slate-400">RA 12009 vs RA 9184</CardDescription>
                </div>
                <div className="mt-4 border-t border-slate-200 dark:border-slate-700"></div>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-slate-600 dark:text-slate-400">
                  Comparison of minimum and maximum calendar days for each procurement stage.
                </p>
              </CardContent>
            </Card>

            <Card
              className="hover:shadow-lg transition-shadow cursor-pointer bg-white dark:bg-slate-800 dark:border-slate-700 dark:hover:border-emerald-600"
              onClick={() => openMatrix('standard-forms')}
            >
              <CardHeader>
                <div className="flex flex-col">
                  <List className="text-emerald-600 dark:text-emerald-400 mb-3" size={32} />
                  <CardTitle className="text-xl dark:text-slate-100">Standard Procurement Forms</CardTitle>
                  <CardDescription className="dark:text-slate-400">RA 12009 & IRR</CardDescription>
                </div>
                <div className="mt-4 border-t border-slate-200 dark:border-slate-700"></div>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-slate-600 dark:text-slate-400">
                  A quick-reference matrix listing the standard procurement forms under RA 12009 and its IRR, with brief details and action points for Procuring Entities.
                </p>
              </CardContent>
            </Card>
          </div>
        </section>
      </div>

      <BottomNav />
    </div>
  );
}
